import { useMemo } from 'react';
import { influencer, outreach, PaginatedApiResponseData, TiktokAudio } from '@round/api';
import { useCountries } from '@round/creatorbase-helpers/api-hooks';
import { useTiktokAudios } from '@round/platforms/tiktok';
import { ConfigurationGroup } from './components/ConfigurationGroup/ConfigurationGroup';
import { getConfigurationGroups } from './helpers';
import styles from './OutreachRequestConfiguration.module.css';

const SECTION_TITLE = 'Request setup';
const SECTION_TITLE_ID = 'outreach-request-setup-title';

/** Names the folded row apart from the section it stands in for, which is still mounted above it. */
const COMPACT_LABEL = 'Request setup summary';

const EMPTY_AUDIOS: Map<number, TiktokAudio> = new Map();

type Props = {
    request: outreach.OutreachRequest;
    /** Folds the section into a single row of summaries once it has scrolled behind the headline. */
    isCompact?: boolean;
};

export const OutreachRequestConfiguration = ({ request, isCompact }: Props) => {
    const audioIds = request.targets.audios;
    const { data: audiosById, isLoading: isLoadingAudios } = useTiktokAudios(
        // A page big enough for every id asked for: a truncated page would leave targets unresolved
        // with nothing in the response to say so.
        { id: audioIds.join(','), page_size: audioIds.length },
        { enabled: audioIds.length > 0, select: getAudiosById }
    );

    const { countries, isLoading: isLoadingCountries } = useCountries();
    const countriesById = useMemo(() => getCountriesById(countries), [countries]);

    const groups = getConfigurationGroups(request, {
        audiosById: audiosById ?? EMPTY_AUDIOS,
        isLoadingAudios,
        countriesById,
        isLoadingCountries,
    });

    if (isCompact) {
        return (
            <section aria-label={COMPACT_LABEL} className={styles.compact}>
                {groups.map((group) => (
                    <ConfigurationGroup key={group.title} {...group} isCompact />
                ))}
            </section>
        );
    }

    return (
        <section aria-labelledby={SECTION_TITLE_ID} className={styles.container}>
            <h2 id={SECTION_TITLE_ID} className={styles.sectionTitle}>
                {SECTION_TITLE}
            </h2>

            <div className={styles.groups}>
                {groups.map((group) => (
                    <ConfigurationGroup key={group.title} {...group} />
                ))}
            </div>
        </section>
    );
};

function getAudiosById(data: PaginatedApiResponseData<TiktokAudio>): Map<number, TiktokAudio> {
    return new Map(data.results.map((audio) => [audio.id, audio]));
}

function getCountriesById(countries: influencer.InfluencerLocation[]): Map<number, influencer.InfluencerLocation> {
    return new Map(countries.map((country) => [country.id, country]));
}
